import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

const CLUSTER_STYLES = {
  dump_site: { color: '#dc2626', label: 'DUMP SITE', icon: '\u2B1F' },
  perpetrator: { color: '#9333ea', label: 'PERPETRATOR', icon: '\u25C8' },
  mp_cluster: { color: '#d97706', label: 'MP GROUP', icon: '\u25C9' },
  migrant_corridor: { color: '#0891b2', label: 'CORRIDOR', icon: '\u25CE' },
};

const PRIORITY_COLOR = {
  critical: '#dc2626',
  high: '#f59e0b',
  medium: '#6b7280',
};

// Zoom-adaptive sizing
function getBaseSize(zoom) {
  if (zoom >= 10) return 40;
  if (zoom >= 6) return 32;
  return 24;
}

function createClusterIcon(cluster, zoom) {
  const style = CLUSTER_STYLES[cluster.type] || CLUSTER_STYLES.dump_site;
  const priority = PRIORITY_COLOR[cluster.priority] || '#6b7280';
  const base = getBaseSize(zoom);
  // Scale slightly by count, but capped by zoom-tier base
  const size = Math.min(base, cluster.count > 10 ? base : cluster.count > 3 ? Math.round(base * 0.85) : Math.round(base * 0.7));
  const fontSize = size > 32 ? 12 : size > 24 ? 10 : 8;
  const pulse = cluster.priority !== 'medium'
    ? `<div style="
        position:absolute; inset:-6px; border-radius:50%;
        border:1px solid ${style.color}44;
        animation:clusterPulse 2s ease-in-out infinite;
      "></div>` : '';

  return L.divIcon({
    className: '',
    iconSize: [size + 16, size + 16],
    iconAnchor: [(size + 16) / 2, (size + 16) / 2],
    html: `
      <style>
        @keyframes clusterPulse {
          0%,100%{transform:scale(1);opacity:.3}
          50%{transform:scale(1.4);opacity:.1}
        }
      </style>
      <div style="
        position:relative;
        width:${size + 16}px; height:${size + 16}px;
        display:flex; align-items:center; justify-content:center;
      ">
        ${pulse}
        <div style="
          width:${size}px; height:${size}px; border-radius:50%;
          background:#0f0f1a;
          border:2px solid ${style.color};
          box-shadow:0 0 8px ${style.color}66;
          display:flex; align-items:center; justify-content:center;
          font-family:monospace; font-weight:700;
          font-size:${fontSize}px;
          color:${style.color};
          position:relative;
        ">
          ${cluster.count > 99 ? '99+' : cluster.count}
          <div style="
            position:absolute; top:-3px; right:-3px;
            width:8px; height:8px; border-radius:50%;
            background:${priority};
            border:1.5px solid #0a0a14;
          "></div>
        </div>
      </div>
    `,
  });
}

function buildGroup(map, clusters, zoom, onClusterClick) {
  const group = L.featureGroup();
  clusters.forEach(cluster => {
    if (!cluster.latitude || !cluster.longitude) return;
    const marker = L.marker(
      [cluster.latitude, cluster.longitude],
      { icon: createClusterIcon(cluster, zoom), zIndexOffset: 1000 }
    );
    const style = CLUSTER_STYLES[cluster.type] || CLUSTER_STYLES.dump_site;
    marker.bindTooltip(`
      <div style="
        font-family:monospace; font-size:11px;
        background:#0f0f1a; color:#e0e0e0;
        border:1px solid ${style.color}44;
        padding:6px 10px; border-radius:4px;
        white-space:nowrap;
      ">
        <span style="color:${style.color}">${style.icon} ${style.label}</span><br/>
        <strong>${cluster.name}</strong><br/>
        <span style="color:#888">${cluster.count} cases linked</span>
      </div>
    `, { className: 'cluster-tooltip', direction: 'top', offset: [0, -8] });
    marker.on('click', () => onClusterClick(cluster));
    group.addLayer(marker);
  });
  return group;
}

export function ClusterLayer({ clusters, onClusterClick, visible }) {
  const map = useMap();
  const layerRef = useRef(null);
  const [mapZoom, setMapZoom] = useState(map.getZoom());

  // Track zoom changes
  useEffect(() => {
    const onZoom = () => setMapZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [map]);

  // Rebuild markers when zoom, visibility, or data changes
  useEffect(() => {
    if (layerRef.current) {
      try {
        if (map.hasLayer(layerRef.current)) map.removeLayer(layerRef.current);
      } catch (e) { /* ignore */ }
      layerRef.current = null;
    }

    if (!visible || !clusters?.length) return;

    const group = buildGroup(map, clusters, mapZoom, onClusterClick);
    group.addTo(map);
    layerRef.current = group;

    return () => {
      if (layerRef.current) {
        try {
          if (map.hasLayer(layerRef.current)) map.removeLayer(layerRef.current);
        } catch (e) { /* ignore */ }
        layerRef.current = null;
      }
    };
  }, [map, clusters, visible, mapZoom, onClusterClick]);

  return null;
}
